// plugins/date.ts
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";
import duration from "dayjs/plugin/duration";
import updateLocale from "dayjs/plugin/updateLocale";
import advancedFormat from 'dayjs/plugin/advancedFormat'

// Import locales
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/pt-br";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(calendar);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(updateLocale);
dayjs.extend(advancedFormat);

// 🟦 English (en)
dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s',
    s: 'now',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1mo',
    MM: '%dmo',
    y: '1y',
    yy: '%dy'
  }
})

// 🇪🇸 Spanish (es)
dayjs.updateLocale('es', {
  relativeTime: {
    future: 'en %s',
    past: 'hace %s',
    s: 'ahora',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1mes',
    MM: '%dmeses',
    y: '1a',
    yy: '%da'
  }
})

// 🇧🇷 Portuguese (pt-br)
dayjs.updateLocale('pt-br', {
  relativeTime: {
    future: 'em %s',
    past: 'há %s',
    s: 'agora',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1mês',
    MM: '%dmeses',
    y: '1a',
    yy: '%da'
  }
})

export default defineNuxtPlugin(() => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const date = {
    // Original dayjs instance for custom usage
    dayjs,

    // Get user's timezone
    timezone: userTimezone,

    // Formatting functions
    formatDate(date: string) {
      return dayjs.utc(date).tz(userTimezone).format("MMM D, YYYY");
    },

    formatDateTime(date: string) {
      return dayjs.utc(date).tz(userTimezone).format("MMM D, YYYY h:mm A");
    },

    formatTime(date: string) {
      return dayjs.utc(date).tz(userTimezone).format("HH:mm A");
    },

    formatDateTimeForApi(date: string) {
      return dayjs.utc(date).tz('utc').format("YYYY-MM-DD HH:mm:ss");
    },

    diffForHumans(date: string) {
      let localDate = dayjs
        .utc(date)
        .tz(userTimezone)
        .format("YYYY-MM-DD HH:mm:ss");
      return dayjs().to(dayjs(localDate));
    },

    dateHasMoreThan30Days(date: string) {
      return dayjs().diff(dayjs.utc(date).tz(userTimezone), 'days') > 30;
    },

    formatLastMessageDate(date: string) {
      let localDate = dayjs.utc(date).tz(userTimezone).format("YYYY-MM-DD HH:mm:ss");
      return dayjs(localDate).fromNow(true);
    },

    formatResponseDelay(seconds: number) {
      return dayjs().add(seconds, "seconds").fromNow(true);
    },

    userTime(t: string) {
      return dayjs().tz(t).format("h:mm A");
    },

    formatOrdinalDay(day: number): string {
      return dayjs().date(day).format('Do')
    }
  };

  return {
    provide: {
      date: date
    }
  };
});